import React, { useState, ChangeEvent } from 'react';

function CompoundInterestCalculator() {
    const [principal, setPrincipal] = useState<string>('');
    const [rate, setRate] = useState<string>('');
    const [time, setTime] = useState<string>('');
    const [periodicContribution, setPeriodicContribution] = useState<string>('');
    const [compoundingFrequency, setCompoundingFrequency] = useState<'annually' | 'semi-annually' | 'quarterly' | 'monthly' | 'daily'>('annually');
    const [results, setResults] = useState<{
        finalAmount: number;
        interestEarned: number;
        effectiveRate: number;
        totalContributions: number;
        returnOnInvestment: number;
    } | null>(null);

    const frequencyMap = {
        'annually': 1,
        'semi-annually': 2,
        'quarterly': 4,
        'monthly': 12,
        'daily': 365
    };

    const calculateInterest = () => {
        if (!principal || !rate || !time) return;

        const P = parseFloat(principal);
        const r = parseFloat(rate) / 100;
        const t = parseFloat(time);
        const n = frequencyMap[compoundingFrequency];
        const PMT = parseFloat(periodicContribution) || 0;

        // Calculate the periodic rate based on compounding frequency
        const periodicRate = r / n;
        
        // Calculate number of total periods
        const totalPeriods = n * t;
        
        // Calculate growth of initial principal
        const principalGrowth = P * Math.pow(1 + periodicRate, totalPeriods);
        
        // Calculate future value of monthly contributions
        // For monthly contributions, we need to adjust the calculation based on compounding frequency
        let contributionsGrowth = 0;
        if (PMT > 0) {
            // Number of contributions per compounding period
            const paymentsPerPeriod = 12 / n;
            
            // Adjust the periodic payment to account for multiple contributions per period
            const adjustedPMT = PMT * paymentsPerPeriod;
            
            // Future value of periodic payments formula
            contributionsGrowth = adjustedPMT * (Math.pow(1 + periodicRate, totalPeriods) - 1) / periodicRate;
        }

        const finalAmount = principalGrowth + contributionsGrowth;
        const totalContributions = P + (PMT * 12 * t); // Initial + all monthly contributions
        const interestEarned = finalAmount - totalContributions;
        const effectiveRate = (Math.pow(1 + r/n, n) - 1) * 100;
        const returnOnInvestment = (interestEarned / totalContributions) * 100;

        setResults({
            finalAmount,
            interestEarned,
            effectiveRate,
            totalContributions,
            returnOnInvestment
        });
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            calculateInterest();
        }
    };

    const clearInputs = () => {
        setPrincipal('');
        setRate('');
        setTime('');
        setPeriodicContribution('');
        setResults(null);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    return (
        <div className="tool">
            <h2>Compound Interest Calculator</h2>
            <div className="content">
                <div className="compound-interest">
                    <div className="compound-interest__section">
                        <h3 className="compound-interest__section-title">Interest Compounding Frequency</h3>
                        <div className="compound-interest__type-selector">
                            <button
                                className={`compound-interest__type-button ${compoundingFrequency === 'annually' ? 'active' : ''}`}
                                onClick={() => setCompoundingFrequency('annually')}
                            >
                                Annually
                            </button>
                            <button
                                className={`compound-interest__type-button ${compoundingFrequency === 'semi-annually' ? 'active' : ''}`}
                                onClick={() => setCompoundingFrequency('semi-annually')}
                            >
                                Semi-Annually
                            </button>
                            <button
                                className={`compound-interest__type-button ${compoundingFrequency === 'quarterly' ? 'active' : ''}`}
                                onClick={() => setCompoundingFrequency('quarterly')}
                            >
                                Quarterly
                            </button>
                            <button
                                className={`compound-interest__type-button ${compoundingFrequency === 'monthly' ? 'active' : ''}`}
                                onClick={() => setCompoundingFrequency('monthly')}
                            >
                                Monthly
                            </button>
                            <button
                                className={`compound-interest__type-button ${compoundingFrequency === 'daily' ? 'active' : ''}`}
                                onClick={() => setCompoundingFrequency('daily')}
                            >
                                Daily
                            </button>
                        </div>
                    </div>

                    <div className="compound-interest__section">
                        <h3 className="compound-interest__section-title">Investment Information</h3>
                        <div className="compound-interest__input-container">
                            <div className="compound-interest__input-group">
                                <label className="compound-interest__label">
                                    Initial Investment Amount ($)
                                </label>
                                <input
                                    type="number"
                                    value={principal}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPrincipal(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter initial amount"
                                    className="compound-interest__input"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            <div className="compound-interest__input-group">
                                <label className="compound-interest__label">
                                    Annual Interest Rate (%)
                                </label>
                                <input
                                    type="number"
                                    value={rate}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setRate(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter interest rate"
                                    className="compound-interest__input"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                />
                            </div>
                            <div className="compound-interest__input-group">
                                <label className="compound-interest__label">
                                    Investment Period (Years)
                                </label>
                                <input
                                    type="number"
                                    value={time}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTime(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter number of years"
                                    className="compound-interest__input"
                                    min="0"
                                    step="0.1"
                                />
                            </div>
                            <div className="compound-interest__input-group">
                                <label className="compound-interest__label">
                                    Monthly Contribution ($)
                                </label>
                                <input
                                    type="number"
                                    value={periodicContribution}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPeriodicContribution(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter monthly contribution"
                                    className="compound-interest__input"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        <div className="compound-interest__actions">
                            <button 
                                onClick={clearInputs}
                                className="compound-interest__clear-button"
                            >
                                Reset Fields
                            </button>
                            <button 
                                onClick={calculateInterest}
                                className="compound-interest__calculate-button"
                            >
                                Calculate Interest
                            </button>
                        </div>
                    </div>

                    {results && (
                        <div className="compound-interest__section">
                            <h3 className="compound-interest__section-title">Investment Results</h3>
                            <div className="compound-interest__results">
                                <div className="compound-interest__result-item">
                                    <div>
                                        <p className="compound-interest__result-label">Total Investment Value</p>
                                        <p className="compound-interest__result-value">{formatCurrency(results.finalAmount)}</p>
                                    </div>
                                </div>
                                <div className="compound-interest__result-item">
                                    <div>
                                        <p className="compound-interest__result-label">Total Contributions</p>
                                        <p className="compound-interest__result-value">{formatCurrency(results.totalContributions)}</p>
                                    </div>
                                </div>
                                <div className="compound-interest__result-item">
                                    <div>
                                        <p className="compound-interest__result-label">Total Interest Earned</p>
                                        <p className="compound-interest__result-value">{formatCurrency(results.interestEarned)}</p>
                                    </div>
                                </div>
                                <div className="compound-interest__result-item">
                                    <div>
                                        <p className="compound-interest__result-label">Effective Annual Rate</p>
                                        <p className="compound-interest__result-value">{results.effectiveRate.toFixed(2)}%</p>
                                    </div>
                                </div>
                                <div className="compound-interest__result-item">
                                    <div>
                                        <p className="compound-interest__result-label">Return on Investment</p>
                                        <p className="compound-interest__result-value">{results.returnOnInvestment.toFixed(2)}%</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CompoundInterestCalculator; 